import { formatDBDate } from "@utils/general/format-db-date";

type DeliveryOrder = {
  customer_name?: string;
  business_unit_name?: string;
  goods_request_note_code?: string;
  way_name?: string;
  delivery_date?: string;
  status?: string | number;
  description?: string;
  detail?: any[];
};

const getStatusLabel = (status: string | number | undefined) => {
  if (status === 1 || status === "1") return "Pending";
  if (status === 2 || status === "2") return "Delivered";
  return status || "-";
};

const getProductCode = (detail: any) =>
  detail?.product_code_prefix
    ? `${detail.product_code_prefix}${detail.product_code || ""}`
    : detail?.product_code || "-";

const getWayLabel = (order: any) =>
  order?.way_name || order?.delivery_way_name || order?.way?.way_name || "-";

const getDeliveryDateLabel = (order: any) => {
  const dateValue =
    order?.delivery_date || order?.delivery_complete_date || order?.issue_date;
  if (!dateValue || dateValue === "-") return "-";

  const parsedDate = new Date(dateValue);
  if (Number.isNaN(parsedDate.getTime())) return "-";

  return formatDBDate(dateValue);
};

const getUnitLabel = (detail: any) =>
  detail?.unit_name ||
  detail?.base_unit_name ||
  detail?.unit?.unit_name ||
  detail?.unit ||
  "-";

const getQtyValue = (detail: any) =>
  Number(
    detail?.qty ??
      detail?.request_qty ??
      detail?.total_issued_qty ??
      detail?.issue_qty ??
      detail?.converted_qty ??
      detail?.base_qty ??
      0
  );

function DeliveryOrderByCustomerTable({ data = [] }: { data?: DeliveryOrder[] }) {
  const groupedCustomers = Object.entries(
    data.reduce((acc: Record<string, DeliveryOrder[]>, order) => {
      const customerLabel = [
        order.customer_name || "Unknown Customer",
        order.business_unit_name || "",
      ]
        .filter(Boolean)
        .join(" / ");

      if (!acc[customerLabel]) {
        acc[customerLabel] = [];
      }

      acc[customerLabel].push(order);
      return acc;
    }, {})
  ).map(([customerLabel, orders]) => {
    const flattenedRows = orders.flatMap((order) =>
      (order.detail || []).map((detail: any) => ({
        refNo: order.goods_request_note_code || "-",
        wayName: getWayLabel(order),
        deliveryDate: getDeliveryDateLabel(order),
        status: getStatusLabel(order.status),
        description: order.description || "-",
        productCode: getProductCode(detail),
        productName: detail?.product_name || "-",
        unitName: getUnitLabel(detail),
        qty: getQtyValue(detail),
        warehouseName: detail?.warehouse_name || "-",
      }))
    );

    return {
      customerLabel,
      orders,
      rows: flattenedRows,
      totals: {
        orders: orders.length,
        items: flattenedRows.length,
        qty: flattenedRows.reduce((sum, row) => sum + row.qty, 0),
      },
    };
  });

  const reportTotals = groupedCustomers.reduce(
    (acc, customerGroup) => {
      acc.customers += 1;
      acc.orders += customerGroup.totals.orders;
      acc.items += customerGroup.totals.items;
      return acc;
    },
    { customers: 0, orders: 0, items: 0 }
  );

  if (!data.length) {
    return (
      <div className="mt-6 rounded-xl border border-gray-200 bg-white p-8 text-center text-gray-500">
        No delivery orders found for the selected date range.
      </div>
    );
  }

  return (
    <div className="mt-6 space-y-6">
      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-blue-700">
            Customers
          </div>
          <div className="mt-2 text-2xl font-bold text-slate-900">
            {reportTotals.customers}
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Orders
          </div>
          <div className="mt-2 text-2xl font-bold text-slate-900">
            {reportTotals.orders}
          </div>
        </div>
      </div>

      {groupedCustomers.map((customerGroup) => (
        <section
          key={customerGroup.customerLabel}
          className="overflow-hidden rounded-2xl border border-slate-200 bg-white"
        >
          <div className="border-b border-slate-200 bg-slate-50 px-5 py-4">
            <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">
                  {customerGroup.customerLabel}
                </h3>
                <p className="mt-1 text-sm text-slate-500">
                  {customerGroup.totals.orders} orders, {customerGroup.totals.items} line
                  items
                </p>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-100 text-slate-600">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Ref No</th>
                  <th className="px-4 py-3 text-left font-semibold">Way</th>
                  <th className="px-4 py-3 text-left font-semibold">Delivery Date</th>
                  <th className="px-4 py-3 text-left font-semibold">Product Code</th>
                  <th className="px-4 py-3 text-left font-semibold">Product Name</th>
                  <th className="px-4 py-3 text-left font-semibold">Unit</th>
                  <th className="px-4 py-3 text-right font-semibold">Qty</th>
                  <th className="px-4 py-3 text-left font-semibold">Allocation Account</th>
                  <th className="px-4 py-3 text-left font-semibold">Status</th>
                  <th className="px-4 py-3 text-left font-semibold">Sales Description</th>
                </tr>
              </thead>
              <tbody>
                {customerGroup.rows.map((row, index) => (
                  <tr
                    key={`${customerGroup.customerLabel}-${row.refNo}-${index}`}
                    className="border-t border-slate-100 align-top hover:bg-slate-50"
                  >
                    <td className="px-4 py-3 whitespace-nowrap text-slate-700">
                      {row.refNo}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-slate-700">
                      {row.wayName}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-slate-700">
                      {row.deliveryDate}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap font-medium text-slate-900">
                      {row.productCode}
                    </td>
                    <td className="px-4 py-3 min-w-[220px] text-slate-700">
                      {row.productName}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-slate-700">
                      {row.unitName}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-slate-900">
                      {row.qty}
                    </td>
                    <td className="px-4 py-3 min-w-[180px] text-slate-700">
                      {row.warehouseName}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-slate-700">
                      {row.status}
                    </td>
                    <td className="px-4 py-3 min-w-[220px] text-slate-700">
                      {row.description}
                    </td>
                  </tr>
                ))}
                <tr className="border-t-2 border-slate-200 bg-slate-50 font-semibold text-slate-900">
                  <td className="px-4 py-3" colSpan={6}>
                    Total
                  </td>
                  <td className="px-4 py-3" colSpan={4}>
                    {customerGroup.totals.orders} orders
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      ))}
    </div>
  );
}

export default DeliveryOrderByCustomerTable;
